import React from 'react'
import {
  Box,
  HStack,
  Text,
  Button,
  Badge,
  Icon,
  useColorModeValue,
  IconButton,
  useColorMode,
  Tooltip,
} from '@chakra-ui/react'
import { ArrowBackIcon, ExternalLinkIcon, MoonIcon, SunIcon } from '@chakra-ui/icons'
import { Link, useNavigate } from 'react-router-dom'
import SpeedcodeLogo from './SpeedcodeLogo'

interface ProblemEditorNavbarProps {
  problemId: string
  onSubmit: () => void
  isSubmitting: boolean
  isPolling: boolean
  isAuthenticated?: boolean
}

export default function ProblemEditorNavbar({
  problemId,
  onSubmit,
  isSubmitting,
  isPolling,
  isAuthenticated = true,
}: ProblemEditorNavbarProps) {
  const navigate = useNavigate()
  const { colorMode, toggleColorMode } = useColorMode()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textColor = useColorModeValue('gray.800', 'gray.200')

  return (
    <Box
      as="nav"
      bg={bgColor}
      borderBottom="1px"
      borderColor={borderColor}
      px={4}
      py={2}
      h="48px"
      flexShrink={0}
      zIndex={20}
      boxShadow="0 1px 2px rgba(0,0,0,0.05)"
    >
      <HStack justify="space-between" h="100%">
        {/* Left: Logo and Back */}
        <HStack spacing={3}>
          <Link to="/">
            <HStack spacing={2} _hover={{ opacity: 0.8 }} transition="opacity 0.2s">
              <SpeedcodeLogo size="navbar" />
              <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                Speedcode
              </Text>
            </HStack>
          </Link>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<ArrowBackIcon />}
            onClick={() => navigate('/problems')}
            fontSize="xs"
            px={2}
            py={1}
            color={textColor}
            _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
          >
            Back
          </Button>
        </HStack>

        {/* Center: Problem Info */}
        <HStack spacing={3}>
          <Text fontSize="sm" fontWeight="medium" color={textColor}>
            {problemId}
          </Text>
          <Badge colorScheme="blue" variant="subtle" fontSize="xs" px={2} py={0.5}>
            C++
          </Badge>
        </HStack>

        {/* Right: Actions */}
        <HStack spacing={2}>
          <Tooltip label={`Switch to ${colorMode === 'light' ? 'dark' : 'light'} mode`}>
            <IconButton
              aria-label="Toggle color mode"
              icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
              size="sm"
              variant="ghost"
              onClick={toggleColorMode}
            />
          </Tooltip>
          <Button
            colorScheme={isAuthenticated ? "green" : "gray"}
            size="sm"
            onClick={onSubmit}
            isLoading={isSubmitting || isPolling}
            loadingText={isSubmitting ? "Submitting..." : "Processing..."}
            isDisabled={!isAuthenticated}
            px={4}
            py={1}
            fontSize="xs"
            fontWeight="medium"
            borderRadius="md"
            _hover={isAuthenticated ? {
              transform: "translateY(-1px)",
              boxShadow: "0 2px 8px rgba(72, 187, 120, 0.3)"
            } : {}}
          >
            {isAuthenticated ? "Submit" : "Login to Submit"}
          </Button>
        </HStack>
      </HStack>
    </Box>
  )
} 